import { useSelector } from "react-redux";

import { formatInTimeZone } from "date-fns-tz";

import { BirthingInformation } from "@/encounters/types/newborn-ipp";
import { selectBirthDateTime } from "@/store/slices/birth";

/**
 * Formats the birth date-time held in the birth slice for a newborn encounter header.
 *
 * Each newborn encounter keeps its own birth time format and day count; this hook owns the slice
 * read, the user's time zone, the `MM/dd/yyyy` birth date, and the empty values shown before a
 * birth time is loaded.
 *
 * @param options.birthTimeFormat - `date-fns` format for the birth time, applied in the user's
 *   time zone.
 * @param options.countDaysSinceBirth - Turns the stored birth date-time into the days-since-birth
 *   number; only called once a birth time is loaded.
 * @returns The birth date, birth time, and days since birth; empty strings and `0` when no birth
 *   time is loaded.
 */
export function useBirthingInformation({
  birthTimeFormat,
  countDaysSinceBirth,
}: BirthingInformationOptions): BirthingInformation {
  const birthDateTime = useSelector(selectBirthDateTime);

  if (!birthDateTime) return { birthDate: "", birthTime: "", timeSinceBirth: 0 };

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const birthDate = new Date(birthDateTime);

  return {
    birthDate: formatInTimeZone(birthDate, userTimeZone, "MM/dd/yyyy"),
    birthTime: formatInTimeZone(birthDate, userTimeZone, birthTimeFormat),
    timeSinceBirth: countDaysSinceBirth(birthDateTime),
  };
}

interface BirthingInformationOptions {
  birthTimeFormat: string;
  countDaysSinceBirth: (birthDateTime: string) => number;
}
